// @flow
import React from 'react';
import Modal from 'react-modal';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import SimpleBar from 'simplebar-react';
import { AreaFilter } from 'components/molecules/areaFilter';
import { BedsFilter } from 'components/molecules/bedsFilter';
import { LocationFilter } from 'components/molecules/locationFilter';
import { PropertyTypeFilter } from 'components/molecules/propertyTypeFilter';
import { SearchFilter } from 'components/molecules/searchFilter';
import { useViewport } from 'components/templates/viewportProvider';
import { ProductBrowserAction } from 'modules/productBrowser/actions';
import { ProductBrowserSelector } from 'modules/productBrowser/selector';
import chevronRightIcon from 'assets/images/rightb.png';
import type {
  ActiveProductFilters,
  LocationCityOption,
  ProductFilters,
  PropertyTypeOption,
  SearchOption,
} from 'types/filter';
// eslint-disable-next-line
import styles from "./styles.scss";

type Props = {
  isOpen: boolean,
  onClosePress: () => void,
  filters: ProductFilters | null,
  activeFilters: ActiveProductFilters | null,
  toggleProductFilter: (type: string, value: number) => void,
  clearProductFilters: () => void,
};

const ProductFiltersModal = (props: Props) => {
  const { isMobile } = useViewport();
  const {
    isOpen,
    onClosePress,
    filters,
    activeFilters,
    toggleProductFilter,
    clearProductFilters,
  } = props;
  if (!filters) {
    return null;
  }

  const onClose = () => {
    onClosePress();
  };

  const toggleLocation = (option: LocationCityOption) => {
    toggleProductFilter('location', option.id);
  };

  const togglePropertyType = (option: PropertyTypeOption) => {
    toggleProductFilter('propertyType', option.id);
  };

  const changeBeds = (values: Array<number>) => {
    toggleProductFilter('beds', values[0]);
  };

  const changeArea = (values: Array<number>) => {
    toggleProductFilter('area', values);
  };

  const toggleProject = (option: SearchOption) => {
    toggleProductFilter('project', option.id);
  };

  const clearAllFilters = e => {
    e.preventDefault();
    clearProductFilters();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClosePress}
      overlayClassName={'modal-overlay normal-scroll'}
      className={`product-filters-overlay-content ${isMobile ? 'mobile' : ''}`}
    >
      <div className={'product-filters-modal'}>
        <a className={'collapse-icon-container'} onClick={onClose}>
          <img src={chevronRightIcon} alt={'icon'} />
        </a>
        {!isMobile && <span className={'modal-title'}>Filters</span>}
        <a className={'modal-button'} onClick={clearAllFilters} href={'#'}>
          Clear All
        </a>
        <SimpleBar autoHide={true} style={{ height: '100%' }}>
          <div className={'product-filters-content'}>
            <div className={'filters-header'}>
              <h3>Looking for something specific?</h3>
              {!isMobile && (
                <p>
                  Filter by location, property type, number of bedrooms,
                  saleable area, and project.
                </p>
              )}
            </div>
            <div className={'filter-set'}>
              <LocationFilter
                activeFilters={activeFilters.location}
                toggleFilter={city => toggleLocation(city)}
                options={filters.location}
              />
              <PropertyTypeFilter
                activeFilters={activeFilters.propertyType}
                toggleFilter={type => togglePropertyType(type)}
                options={filters.propertyType}
              />
              <BedsFilter
                min={filters.beds.min}
                max={filters.beds.max}
                values={activeFilters.beds ? [activeFilters.beds] : null}
                onChange={values => changeBeds(values)}
              />
              <AreaFilter
                min={filters.area.min}
                max={filters.area.max}
                values={
                  activeFilters.area || [filters.area.min, filters.area.max]
                }
                onChange={values => changeArea(values)}
              />
              <SearchFilter
                title={'Projects'}
                options={filters.projects}
                values={activeFilters.projects}
                onClick={option => toggleProject(option)}
              />
            </div>
          </div>
        </SimpleBar>
      </div>
    </Modal>
  );
};

const mapStateToProps = state => {
  return {
    filters: ProductBrowserSelector.getProductFilters(state),
    activeFilters: ProductBrowserSelector.getActiveProductFilters(state),
  };
};

const mapDispatchToProps = dispatch => {
  const { toggleProductFilter, clearProductFilters } = ProductBrowserAction;
  return bindActionCreators(
    {
      toggleProductFilter,
      clearProductFilters,
    },
    dispatch
  );
};

const connectedComponent = connect(
  mapStateToProps,
  mapDispatchToProps
)(ProductFiltersModal);

export { connectedComponent as ProductFiltersModal };
